import axios from "axios";
import { store } from "../../redux/store";
import { setUserAuth, setRequestSignature, setGuestToken, setBypassToken } from "../../redux/slices/authSlice";
import { setStatusPopup } from "../../redux/slices/statusPopupSlice";
import { persistor } from "../../redux/store";
import { decrypt } from "../../../utils/crypto";

const axiosInstance = axios.create({
  baseURL:process.env.REACT_APP_API_URL,
});
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}
// Add a request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Remove "headers" from request body so it is never sent in payload
    // if (
    //   config.data &&
    //   typeof config.data === "object" &&
    //   !(config.data instanceof FormData) &&
    //   "headers" in config.data
    // ) {
    //   const { headers: _headers, ...rest } = config.data;
    //   config.data = Object.keys(rest).length ? rest : undefined;
    // }

    // Always read latest token (prefer axios defaults, then sessionStorage, then Redux)
    // Falls back to the pre-login QRC checkpoint token when there's no normal login session.
    const sessionToken = store?.getState()?.auth?.token || store?.getState()?.auth?.qrcToken;
    // Apply-now journey: until a new token arrives (bypass-login / x-new-token
    // → setUserAuth clears it), send the guestToken — from
    // api/v1/user-lead/create (see response interceptor) or the partner
    // apply-now?t=... link's token. It wins over any older session token.
    const guestToken = store?.getState()?.auth?.guestToken;
    const token = guestToken || sessionToken;


    if (token) {
      config.headers.Authorization = token;
    }

    const triggerData = store?.getState()?.auth?.triggerData;
    const isTriggerDataEmpty =
      !triggerData ||
      (typeof triggerData === "object" && Object.keys(triggerData).length === 0);
    if (isTriggerDataEmpty) {
      config.headers["application-type"] = "applicant";
    } else {
      config.headers["application-type"] =
        triggerData?.toUser === false ? "coapplicant" : "applicant";
    }
    
    config.headers["x-source"] = "web";
    config.headers["x-channel"] = "diy";
    if (config.data instanceof FormData) {
      // Explicitly delete so Axios's default 'application/json' doesn't leak through;
      // the browser will set multipart/form-data with the correct boundary automatically.
      delete config.headers["Content-Type"];
    } else {
      config.headers["Content-Type"] = "application/json";
    }
    // Unchanged for existing flows: x-api-key whenever there's no real session
    // (guest-token calls keep sending it too).
    //if(!sessionToken){
      //config.headers["x-api-key"] = process.env.REACT_APP_X_API_KEY;
    //}
    config.headers["authorization"] = token;

    // Add x-request-signature from auth store (captured from /user/loan-type-permission response)
    const requestSignature = store?.getState()?.auth?.requestSignature;
    if (requestSignature) {
      config.headers["x-request-signature"] = requestSignature;
    }

    // bypassToken captured from otp-verify / coapplicant-otp-verify is the
    // Authorization for bypass-login only (replaces the session/guest token
    // on that one call).
    const bypassToken = store?.getState()?.auth?.bypassToken;
    if (bypassToken && String(config?.url || "").includes("user-lead/bypass-login")) {
      const bypassAuth = /^bearer\s/i.test(bypassToken)
        ? bypassToken
        : `Bearer ${bypassToken}`;
      config.headers.Authorization = bypassAuth;
      config.headers["authorization"] = bypassAuth;
    }

    // Per-request Authorization passed by the caller as config.authOverride
    // (e.g. qrc/user-verification with the token from qrc/otp/verify).
    const authOverride = (config as any)?.authOverride;
    if (typeof authOverride === "string" && authOverride.trim()) {
      const overrideAuth = /^bearer\s/i.test(authOverride.trim())
        ? authOverride.trim()
        : `Bearer ${authOverride.trim()}`;
      config.headers.Authorization = overrideAuth;
      config.headers["authorization"] = overrideAuth;
    }

    // config.headers["x-source"] = "web";

    // You can modify request headers or perform other operations before sending the request
    // For example, you can add authentication tokens if needed
    return config;
  },
  (error) => {
    // Do something with request error
    return Promise.reject(error);
  }
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // qrc/* endpoints (pre-login Help Center flow) must never promote a session to a
    // normal authenticated one via this side channel — that would silently flip
    // authStatus to true for what should stay a QRC-only session (see QrcRoutes).
    const isQrcRequest = response?.config?.url?.includes("/qrc/");
    const newToken =
      !isQrcRequest &&
      (response?.headers?.["x-new-token"] ?? response?.headers?.["X-New-Token"]);
    // Forwarded to the React Native shell regardless of isQrcRequest: this is the parent app's
    // own token, not a promotion of this session, so the QRC guard above doesn't apply to it.
    const mobileToken =
      response?.headers?.["x-parent-new-token"] ?? response?.headers?.["X-Parent-New-Token"];
    if (mobileToken && mobileToken !== "undefined" && mobileToken !== "null") {
      if (window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "MOBILE_TOKEN", token: mobileToken })
        );
      }
    }
    if (newToken) {
      // Update Redux auth state (also syncs sessionStorage via reducer)
      let trimmedToken = newToken.trim();
  
      // Case-insensitive check for 'bearer ' prefix
      if (trimmedToken.toLowerCase().startsWith('bearer')) {
        trimmedToken=trimmedToken;
      }else{
        trimmedToken=`Bearer ${trimmedToken}`;
      }
      store?.dispatch(setUserAuth(trimmedToken));

      // Also update axios defaults so subsequent requests pick up immediately
      axiosInstance.defaults.headers.common["authorization"] = trimmedToken;
      axiosInstance.defaults.headers.common["Authorization"] = trimmedToken;
    }

    // Apply-now journey: user-lead/create returns a guestToken — keep it (only
    // while there's no real session) so every following call, up to and
    // including bypass-login, is sent with it as Authorization.
    const isCreateLead = /api\/v1\/user-lead\/create(?:$|\?)/.test(
      String(response?.config?.url || "")
    );
    const responseGuestToken = response?.data?.guestToken;
    if (
      isCreateLead &&
      typeof responseGuestToken === "string" &&
      responseGuestToken.trim() &&
      !store?.getState()?.auth?.token
    ) {
      const trimmedGuestToken = responseGuestToken.trim();
      store?.dispatch(
        setGuestToken(
          trimmedGuestToken.toLowerCase().startsWith("bearer")
            ? trimmedGuestToken
            : `Bearer ${trimmedGuestToken}`
        )
      );
    }

    // otp-verify / coapplicant-otp-verify return an akSessionRef (formerly
    // bypassToken) that the next bypass-login call sends as Authorization
    // (see request interceptor).
    const responseUrl = String(response?.config?.url || "");
    const isOtpVerifyForBypass =
      /api\/v1\/user\/otp-verify(?:$|\?)/.test(responseUrl) ||
      /api\/v1\/co-applicant\/coapplicant-otp-verify(?:$|\?)/.test(responseUrl);
    if (isOtpVerifyForBypass) {
      const responseData = response?.data;
      const getSessionRef = (obj: any) => obj?.akSessionRef ?? obj?.bypassToken;
      const responseBypassToken =
        getSessionRef(responseData) ??
        getSessionRef(responseData?.data) ??
        (Array.isArray(responseData?.data)
          ? getSessionRef(responseData.data.find((item: any) => getSessionRef(item)))
          : undefined);
      if (typeof responseBypassToken === "string" && responseBypassToken.trim()) {
        store?.dispatch(setBypassToken(responseBypassToken.trim()));
      }
    }

    // Capture x-request-signature from loan-type-permission or bypass-login response and save to Redux
    const isLoanTypePermission =
      response?.config?.url?.includes("loan-type-permission");
    const isBypassLogin = response?.config?.url?.includes("bypass-login");
     const isGuestLogin = response?.config?.url?.includes("guest-login");

    // guest-login's token always replaces the current one (partner/guest or an
    // older session): setUserAuth stores it and clears the guest token, so
    // every call after login carries the new token.
    if (isGuestLogin && response?.data?.status === "success") {
      try {
        const loginData: any = JSON.parse(decrypt(response?.data?.data));
        const loginToken = String(loginData?.token || "").trim();
        if (loginToken) {
          const bearerToken = loginToken.toLowerCase().startsWith("bearer")
            ? loginToken
            : `Bearer ${loginToken}`;
          store?.dispatch(setUserAuth(bearerToken));
          store?.dispatch(setGuestToken(null));
          axiosInstance.defaults.headers.common["authorization"] = bearerToken;
          axiosInstance.defaults.headers.common["Authorization"] = bearerToken;
        }
      } catch (e) {
        console.error("Failed to read guest-login token", e);
      }
    }
    const signature =
      response?.headers?.["x-request-signature"] ??
      response?.headers?.["X-Request-Signature"];
    if ((isLoanTypePermission || isBypassLogin || isGuestLogin) && signature) {
      store?.dispatch(setRequestSignature(signature));
    }

    // Any status code that lies within the range of 2xx causes this function to trigger
    // You can modify response data before it's returned
    return response;
  },
  (error) => {
    // Any status codes that fall outside the range of 2xx cause this function to trigger
    // Handle error responses
    if (error?.response?.status === 409) {
      sessionStorage?.clear();
      localStorage.removeItem(" ");
      // store.dispatch({ type: PURGE, key: 'root' });
      store.dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
          anotherDeviceAuth: true,
        })
      );
      if (window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "LOGOUT_ANOTHERDEVICE" })
        );
      }
      setTimeout(() => {
        persistor.purge(); // ✅ c
      }, 1000); // wait for UI to render popup
      // Clear all cookies
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
      });
      return Promise.reject(error);
    } else if (error?.response?.status === 401) {

      sessionStorage?.clear();
      localStorage.removeItem("selectedIndex");
      store.dispatch({ type: "selectedIndex/reset" });
      setTimeout(() => {
        persistor.purge(); // ✅ c
      }, 1000);
      if(window.location.pathname !== "/login") {
        store.dispatch(
          setStatusPopup({
            status: "error",
            message: error?.response?.data?.message,
            anotherDeviceAuth: true,
          })
        );
      }
      if (window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: "USER_LOGOUT" })
        );
      }
      // Clear all cookies
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
      });
      return Promise.reject(error);
    } else {
      return Promise.reject(error);
    }
  }
);

export default axiosInstance;
